import React, { useState } from "react";
import AgentEffectivenessSearch from "./agenteffectiveness/AgentEffectivenessSearch";
import DoughnutChart from "./agenteffectiveness/DoughnutChart";
import YTDSalesChart from "./agenteffectiveness/YTDSalesChart";
import SalesPipelineChart from "./agenteffectiveness/SalesPipelineChart";
import FadeIn from "./compliance/FadeIn";
import { Grid as GridLoading } from "react-loader-spinner";
import Markdown from "react-markdown";
import { styled } from "@mui/material/styles";
import { theme } from "../theme";
import "../assets/styles/AgentEffectiveness.scss";

// Import MUI elements
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Divider from "@mui/material/Divider";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Modal from "@mui/material/Modal";
import Select from "@mui/material/Select";
import Skeleton from "@mui/material/Skeleton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { Grid, Paper } from "@mui/material";

// Import framework to manage datepicker
import dayjs from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

// Import icons
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import KeyboardDoubleArrowDownIcon from "@mui/icons-material/KeyboardDoubleArrowDown";
import KeyboardDoubleArrowUpIcon from "@mui/icons-material/KeyboardDoubleArrowUp";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import SendIcon from "@mui/icons-material/Send";
import SummarizeIcon from "@mui/icons-material/Summarize";

const Item = styled(Paper)(({ theme }) => ({
  padding: 10,
}));

const BoxContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  backgroundColor: theme.palette.background.paper,
}));

const TranscriptModalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 1250,
  height: 750,
  bgcolor: "background.paper",
  border: "1px solid #000",
  p: 4,
  overflow: "scroll",
  display: "block",
};

const ModalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 850,
  height: 600,
  bgcolor: "background.paper",
  border: "1px solid #000",
  p: 4,
  overflow: "scroll",
  display: "block",
};

// Mock data
const mockAdvisorData = {
  advisor_name: "Michael Chen",
  advisor_id: "WAMCH123",
  advisor_email: "Michael.Chen@wealthmgmt.com",
  advisor_address: "Wealth Management Division, New York",
};

const mockCallSummaryData = {
  total_calls: 45,
  successful_calls: 31,
  voicemail: 8,
  busy: 6,
};

const mockPerformanceData = {
  Qualified_Prospect_Conversations: {
    Agent_value: "14",
    Top_agents_percent: "11",
    Top_decile: "16",
    description: "# of prospect calls with clear investment potential"
  },
  Alternative_Products_Introduced: {
    Agent_value: "80",
    Top_agents_percent: "58",
    Top_decile: "92",
    description: "% of interactions where private markets were positioned"
  },
  Solutions_Offered_per_Prospect: {
    Agent_value: "1.5",
    Top_agents_percent: "1.2",
    Top_decile: "1.8",
    description: "Depth of pitch – showing tailored solutions, not just volume"
  },
  Solution_Acceptance_Rate: {
    Agent_value: "68.4",
    Top_agents_percent: "56.9",
    Top_decile: "76.2",
    description: "Persuasiveness and fit of offering"
  },
  Time_to_Convert: {
    Agent_value: "7.2",
    Top_agents_percent: "10.5",
    Top_decile: "6.1",
    description: "Efficiency in moving from pitch to commitment"
  },
  Onboarding_Completion_Rate: {
    Agent_value: "92",
    Top_agents_percent: "75",
    Top_decile: "96",
    description: "Follow-through and frictionless handoff to advisors"
  },
  Missed_Opportunities_Identified: {
    Agent_value: "2",
    Top_agents_percent: "3.8",
    Top_decile: "1",
    description: "Count of conversations where no next step occurred despite interest"
  },
};

const mockMissedOpportunities = [
  {
    date: "2024-01-27",
    transcript_id: "204",
    sentiment: "7",
    missed_opportunity: "No follow-up sent",
    suggested_next_step: "Send personalized recap with proposal",
  },
  {
    date: "2024-01-28",
    transcript_id: "202",
    sentiment: "7",
    missed_opportunity: "Objection on fees",
    suggested_next_step: "Prep competitive analysis response",
  },
];

const mockAIInsights = `## Key Categories Feedback

### 1. Alternative Investment Engagement
You discussed private equity, private credit, and real estate strategies in **80%** of eligible conversations, compared to a peer average of 58%. You effectively positioned fund timelines, lock-up structures, and diversification benefits—demonstrating strong fluency in alternatives.

### 2. Life Event Triggered Advice
You identified and responded to key client life events—such as retirement, business transitions, and estate planning—at a rate of **0.88**, surpassing the peer average of 0.65. This enabled highly personalized and timely advisory conversations.

### 3. Financial Planning Adjustments
In **92%** of relevant calls, you revisited or updated clients' financial plans after a change in goals, income, or risk tolerance. This compares favorably to the peer average of 75%, showing your adaptability and planning precision.

## Goal-Based Promotion Feedback

### 1. Relevant Solution Offer Rate
You offered at least one suitable financial solution—such as an interval fund, 1031 exchange, or insurance referral—in **100%** of qualifying calls. The peer average was 87.5%, highlighting your strong goal alignment.

### 2. Client Acceptance Rate
Clients accepted your recommendations **68.4%** of the time, exceeding the peer average of 56.9%. This indicates high trust and confidence in your advice.

### 3. Offer Conversion Metrics
Across 12 client conversations, you made 9 financial recommendations (6 alternatives-focused), with 7 accepted. This far exceeds the peer averages of 4.2 recommendations and 3.1 acceptances.

## Summary Feedback – Overall Performance
You consistently deliver tailored, client-aligned guidance with an emphasis on sophisticated alternatives strategies. Your ability to engage around life events, adapt financial plans, and offer well-timed, goal-driven solutions sets you apart. Client sentiment across your calls averaged **8.9 out of 10**—significantly above the peer benchmark of 7.4—demonstrating the strength of your communication, trust-building, and advisory value.`;

function formatDate(dateString: string) {
  const months = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ];
  const date = new Date(dateString);
  const day = date.getDate();
  const month = months[date.getMonth()];
  const year = date.getFullYear();
  return `${day} ${month} ${year}`;
}

function MockWamAgentEffectiveness({ parentToChild }: { parentToChild: any }) {
  const { mode } = parentToChild;

  const [selectedOption, setSelectedOption] = useState<string>("Last 7 days");
  const [endDate, setEndDate] = useState<dayjs.Dayjs>(dayjs());
  const [startDate, setStartDate] = useState<dayjs.Dayjs>(dayjs().subtract(7, "days"));
  const [expandClicked, setExpandClicked] = useState<boolean>(false);

  // Variables for sending a report
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [subject, setSubject] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [comments, setComments] = useState<string>("");
  const [frequency, setFrequency] = useState<string>("Daily");
  const [recurrences, setRecurrences] = useState<string>("1");
  const [rangeStartDate, setRangeStartDate] = useState<dayjs.Dayjs>(dayjs());
  const [rangeEndDate, setRangeEndDate] = useState<dayjs.Dayjs>(dayjs());
  const Today = dayjs();

  // Transcript modal state
  const [open, setOpen] = useState<boolean>(false);
  const [selectedTranscript, setSelectedTranscript] = useState<any>(null);

  const handleEndDateChange = (selectedOption: string, newDate: dayjs.Dayjs) => {
    setEndDate(newDate);
    let calculatedStartDate;

    if (selectedOption === "Last 7 days") {
      calculatedStartDate = newDate.subtract(6, "days");
    } else if (selectedOption === "Last 30 days") {
      calculatedStartDate = newDate.subtract(30, "days");
    } else if (selectedOption === "Last 60 days") {
      calculatedStartDate = newDate.subtract(60, "days");
    } else {
      calculatedStartDate = newDate;
    }
    
    setStartDate(calculatedStartDate);
    setRangeStartDate(calculatedStartDate);
  };

  const handleReset = () => {
    setSelectedOption("Last 7 days");
    setEndDate(dayjs());
    setStartDate(dayjs().subtract(7, "days"));
    setRangeStartDate(dayjs().subtract(7, "days"));
  };

  const handleSendReport = () => {
    setIsModalOpen(false);
  };

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const handleOpen = (row: any) => {
    setSelectedTranscript(row);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <FadeIn transitionDuration={700}>
      <div className='agenteffect-root container'>
        <FadeIn transitionDuration={700}>
          <Box sx={{ flexGrow: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Item className='exec-navbar-filters'>
                  <Box sx={{ flexGrow: 1 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={6} sm={6} md={3} lg={1.5} xl={1.5}>
                        <FormControl fullWidth>
                          <InputLabel id='select-label'>
                            Activity Analysis Period
                          </InputLabel>
                          <Select
                            labelId='select-label'
                            id='select'
                            value={selectedOption}
                            onChange={(e) => {
                              setSelectedOption(e.target.value);
                              handleEndDateChange(e.target.value, endDate);
                            }}
                          >
                            <MenuItem value='Last 7 days'>Last 7 days</MenuItem>
                            <MenuItem value='Last 30 days'>Last 30 days</MenuItem>
                            <MenuItem value='Last 60 days'>Last 60 days</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={6} sm={6} md={3} lg={1.5} xl={1.5}>
                        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale='en'>
                          <DatePicker
                            label='Report Date'
                            value={endDate}
                            onChange={(e) => {
                              if (e) {
                                handleEndDateChange(selectedOption, e);
                              }
                            }}
                          />
                        </LocalizationProvider>
                      </Grid>
                      <Grid item xs={12} sm={12} md={6} lg={9} xl={9}>
                        <div className='exec-buttons'>
                          <Button
                            className='agenteffect-nav-btn exec-nav-btn'
                            variant='contained'
                            endIcon={<RestartAltIcon />}
                            onClick={handleReset}
                          >
                            Reset Filters
                          </Button>
                          <Button
                            className='agenteffect-nav-btn exec-nav-btn'
                            variant='contained'
                            endIcon={<SendIcon />}
                            onClick={openModal}
                          >
                            Send Report
                          </Button>
                        </div>
                      </Grid>

                      <Modal
                        className='exec-send-modal modal-global'
                        open={isModalOpen}
                        onClose={closeModal}
                        aria-labelledby='modal-modal-title'
                        aria-describedby='modal-modal-description'
                      >
                        <Box sx={ModalStyle}>
                          <h3>
                            <SendIcon /> Report Form
                          </h3>
                          <div>
                            <TextField
                              label='Subject'
                              variant='outlined'
                              fullWidth
                              value={subject}
                              onChange={(e) => setSubject(e.target.value)}
                              margin='normal'
                            />
                            <TextField
                              label='Email'
                              variant='outlined'
                              fullWidth
                              value={email}
                              onChange={(e) => setEmail(e.target.value)}
                              margin='normal'
                            />
                            <TextField
                              label='Additional Comments'
                              variant='outlined'
                              fullWidth
                              multiline
                              rows={4}
                              value={comments}
                              onChange={(e) => setComments(e.target.value)}
                              margin='normal'
                            />
                            <FormControl fullWidth className='send-report-dropdown'>
                              <InputLabel id='select-label'>Report Frequency</InputLabel>
                              <Select
                                labelId='select-label'
                                id='select'
                                value={frequency}
                                onChange={(e) => setFrequency(e.target.value)}
                              >
                                <MenuItem value='Daily'>Daily</MenuItem>
                                <MenuItem value='Weekly'>Weekly</MenuItem>
                                <MenuItem value='Monthly'>Monthly</MenuItem>
                              </Select>
                            </FormControl>
                            <TextField
                              label='Num of Recurrences'
                              variant='outlined'
                              fullWidth
                              type='number'
                              value={recurrences}
                              onChange={(e) => {
                                const value = parseInt(e.target.value);
                                if (!isNaN(value) && value >= 0 && value <= 365) {
                                  setRecurrences(value.toString());
                                } else if (e.target.value === "") {
                                  setRecurrences("");
                                }
                              }}
                              margin='normal'
                              inputProps={{ inputMode: "numeric", max: 365 }}
                            />
                            <p className='text-dark-mode'>Recurrence Range:</p>
                            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale='en'>
                              <div className='send-report-datepickers'>
                                <DatePicker
                                  className='exec-date-picker'
                                  label='Start'
                                  value={rangeStartDate}
                                  onChange={(date) => {
                                    if (date && date.isBefore(Today, "day")) {
                                      setRangeStartDate(Today);
                                    } else {
                                      setRangeStartDate(date || Today);
                                    }
                                  }}
                                />
                                <span className='text-dark-mode'> to </span>
                                <DatePicker
                                  className='exec-date-picker'
                                  label='End'
                                  value={rangeEndDate}
                                  onChange={(date) => {
                                    if (date && date.isBefore(Today, "day")) {
                                      setRangeEndDate(Today);
                                    } else if (rangeStartDate && date && date.isBefore(rangeStartDate, "day")) {
                                      setRangeEndDate(rangeStartDate);
                                    } else {
                                      setRangeEndDate(date || Today);
                                    }
                                  }}
                                />
                              </div>
                            </LocalizationProvider>
                          </div>
                          <div>
                            <Button
                              className='exec-send-btn'
                              variant='contained'
                              onClick={handleSendReport}
                            >
                              Send
                            </Button>
                          </div>
                        </Box>
                      </Modal>
                    </Grid>
                  </Box>
                </Item>
              </Grid>
            </Grid>

            <Grid className='agenteffect-container-wrapper' container spacing={2}>
              <Grid className='agenteffect-paper-wrapper' item xs={12} sm={12} md={6} lg={4} xl={4}>
                <BoxContainer className='agenteffect-left-container'>
                  <h3>Advisor Statistics</h3>
                  <Grid container spacing={2} className='agent-profile'>
                    <Grid className='profile-img' item xs={12} sm={12} md={6} lg={3} xl={3}>
                      <Avatar className='profile_avatar' />
                    </Grid>
                    <Grid item xs={12} sm={12} md={6} lg={9} xl={9}>
                      <div className='agent-profile-info'>
                        <h2>{mockAdvisorData.advisor_name}</h2>
                        <p>
                          {mockAdvisorData.advisor_id} | {mockAdvisorData.advisor_email}
                        </p>
                        <p>{mockAdvisorData.advisor_address}</p>
                      </div>
                    </Grid>
                  </Grid>
                  <Divider className='agenteffect-divider' />
                  <h3>Total Calls Handled</h3>
                  <div>
                    <React.StrictMode>
                      <DoughnutChart parentToChild={{ mode, callSummaryData: mockCallSummaryData }} />
                    </React.StrictMode>
                  </div>
                  <Divider className='agenteffect-divider' />
                  <YTDSalesChart parentToChild={{ mode }} />
                  <Divider className='agenteffect-divider' />
                  <SalesPipelineChart parentToChild={{ mode }} />
                </BoxContainer>
              </Grid>

              <Grid className='agenteffect-paper-wrapper' item xs={12} sm={12} md={6} lg={3.5} xl={3.5}>
                <BoxContainer className='agenteffect-paper-container container-middle'>
                  <h3>
                    Advisor Performance Metrics
                    {mode === "dark" ? (
                      <span className='shine'>
                        <AutoAwesomeIcon />
                      </span>
                    ) : (
                      <span className='ai-driven'>
                        <AutoAwesomeIcon />
                      </span>
                    )}
                  </h3>
                  <TableContainer className='agenteffect-table-container middle-table-container' component={Paper}>
                    <Table className='agenteffect-table middle-table' size='small' aria-label='a dense table'>
                      <TableHead>
                        <TableRow>
                          <TableCell style={{ width: "55%" }} align='left'>
                            Metric
                          </TableCell>
                          <TableCell style={{ width: "15%" }} align='center'>
                            You
                          </TableCell>
                          <TableCell style={{ width: "15%" }} align='center'>
                            Team Avg
                          </TableCell>
                          <TableCell style={{ width: "15%" }} align='center'>
                            Top Performer
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {Object.entries(mockPerformanceData).map(([key, data]: [string, any]) => (
                          <TableRow key={key} sx={{ "& > *": { borderBottom: "unset" } }}>
                            <TableCell style={{ width: "55%" }} align='left'>
                              {key.replace(/_/g, " ")}
                            </TableCell>
                            <TableCell style={{ width: "15%" }} align='center'>
                              {key.includes("Rate") || key.includes("Introduced") 
                                ? `${data.Agent_value}%` 
                                : data.Agent_value}
                            </TableCell>
                            <TableCell style={{ width: "15%" }} align='center'>
                              {key.includes("Rate") || key.includes("Introduced") 
                                ? `${data.Top_agents_percent}%` 
                                : data.Top_agents_percent}
                            </TableCell>
                            <TableCell style={{ width: "15%" }} align='center'>
                              {key.includes("Rate") || key.includes("Introduced") 
                                ? `${data.Top_decile}%` 
                                : data.Top_decile}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <h3>Missed Opportunities</h3>
                  <TableContainer className='agenteffect-sample-transcripts'>
                    <Table className='compliance-clickable-table' size='small' aria-label='a dense table'>
                      <TableHead>
                        <TableRow>
                          <TableCell style={{ width: "20%" }} align='left'>
                            Date
                          </TableCell>
                          <TableCell style={{ width: "15%" }} align='center'>
                            ID
                          </TableCell>
                          <TableCell style={{ width: "35%" }} align='center'>
                            Missed Opportunity
                          </TableCell>
                          <TableCell style={{ width: "30%" }} align='center'>
                            Next Step
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {mockMissedOpportunities.map((item: any, index: number) => (
                          <TableRow
                            key={index}
                            component='tr'
                            sx={{ "& > *": { borderBottom: "unset" } }}
                            onClick={() => handleOpen(item)}
                          >
                            <TableCell style={{ width: "20%" }} align='left'>
                              {formatDate(item.date)}
                            </TableCell>
                            <TableCell style={{ width: "15%" }} align='center'>
                              {item.transcript_id}
                            </TableCell>
                            <TableCell style={{ width: "35%", fontSize: "11px" }} align='center'>
                              {item.missed_opportunity}
                            </TableCell>
                            <TableCell style={{ width: "30%", fontSize: "11px" }} align='center'>
                              {item.suggested_next_step}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </BoxContainer>
              </Grid>

              <Grid className='agenteffect-paper-wrapper' item xs={12} sm={12} md={12} lg={4.5} xl={4.5}>
                <BoxContainer className='agenteffect-paper-container agenteffect-container-right-section'>
                  <h3>
                    AI Insights and Recommendations
                    {mode === "dark" ? (
                      <span className='shine'>
                        <AutoAwesomeIcon />
                      </span>
                    ) : (
                      <span className='ai-driven'>
                        <AutoAwesomeIcon />
                      </span>
                    )}
                  </h3>
                  <div className='markdown-section agent-effectiveness-feedback'>
                    <Markdown
                      className={`expand-agent-feedback ${expandClicked ? "expand-clicked" : ""}`}
                    >
                      {mockAIInsights}
                    </Markdown>
                    <span
                      className='expand-readmore-btn'
                      onClick={() => setExpandClicked((prev) => !prev)}
                    >
                      {expandClicked ? (
                        <>
                          Show Less <KeyboardDoubleArrowUpIcon />
                        </>
                      ) : (
                        <>
                          Show More <KeyboardDoubleArrowDownIcon />
                        </>
                      )}
                    </span>
                  </div>
                </BoxContainer>
              </Grid>
            </Grid>

            <Grid className='agenteffect-container-wrapper' container spacing={2}>
              <Grid item xs={12}>
                <Item className='ops-card ops-search-section'>
                  <div className='ops-search-header'>
                    <h3>Transcript Semantic Search</h3>
                  </div>
                  <React.StrictMode>
                    <AgentEffectivenessSearch
                      parentToChild={{
                        startDate,
                        endDate,
                        selectedOption,
                        mode,
                      }}
                    />
                  </React.StrictMode>
                </Item>
              </Grid>
            </Grid>
          </Box>
        </FadeIn>
      </div>

      {selectedTranscript && (
        <Modal
          open={open}
          onClose={handleClose}
          aria-labelledby='modal-modal-title'
          aria-describedby='modal-modal-description'
          className='modal-global'
        >
          <Box className='modal-style' sx={TranscriptModalStyle}>
            <div className='modal-header'>
              <h3>
                <SummarizeIcon /> Opportunity Details
              </h3>
            </div>
            <Box sx={{ flexGrow: 1 }}>
              <Card variant='outlined' className='get-insights-card'>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Transcript ID: {selectedTranscript.transcript_id}
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    Date: {formatDate(selectedTranscript.date)}
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    Sentiment Score: {selectedTranscript.sentiment}/10
                  </Typography>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    Missed Opportunity:
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {selectedTranscript.missed_opportunity}
                  </Typography>
                  <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                    Suggested Next Step:
                  </Typography>
                  <Typography variant="body1">
                    {selectedTranscript.suggested_next_step}
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          </Box>
        </Modal>
      )}
    </FadeIn>
  );
}

export default MockWamAgentEffectiveness; 